import React, { useState, useMemo, useEffect } from 'react';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import ReviewsSection from '../components/ReviewsSection';
import SearchBar from '../components/SearchBar';
import FilterBar from '../components/FilterBar';
import CategoryPills from '../components/CategoryPills';
import ProductGrid from '../components/ProductGrid';
import SignInModal from '../components/SignInModal';
import FloatingCheckoutButton from '../components/FloatingCheckoutButton';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';
import BackButton from '../components/BackButton';
import ProductDetailModal from '../components/ProductDetailModal';
import WriteReviewModal from '../components/WriteReviewModal';
import PictureSearch from '../components/PictureSearch';
import AIChatWidget from '../components/AIChatWidget';
import Profile from './Profile';
import GiftCards from './GiftCards';
import MyOrders from './MyOrders';
import InStoreShopping from './InStoreShopping';
import OrdersPortal from './OrdersPortal';
import Offers from './Offers';
import Checkout from './Checkout';
import AdminDashboard from './AdminDashboard';
import AdminProducts from './AdminProducts';
import AdminOrders from './AdminOrders';
import DriversDashboard from './DriversDashboard';
import DriversOnboarding from './DriversOnboarding';
import DriversNavigation from './DriversNavigation';
import PaymentConfirmationPortal from './PaymentConfirmationPortal';
import UserManagement from './UserManagement';
import RoleManagement from './RoleManagement';
import SystemMaintenance from './SystemMaintenance';
import PromotionalNotifications from './PromotionalNotifications';
import BrandingSettings from './BrandingSettings';
import StripeSettings from './StripeSettings';
import GeneralOverseer from './GeneralOverseer';
import OrderTracking from './OrderTracking';
import OrderStatus from './OrderStatus';
import AdminReports from './AdminReports';
import PickingList from './PickingList';
import PickingPortal from './PickingPortal';
import PaymentPortal from './PaymentPortal';
import DriverDelivery from './DriverDelivery';
import LiveOrderTracking from './LiveOrderTracking';
import TermsAndConditions from './TermsAndConditions';
import AppUsersPortal from './AppUsersPortal';
import IdenticalProductsPortal from './IdenticalProductsPortal';
import DeviceSettingsPortal from './DeviceSettingsPortal';
import POSCashier from './POSCashier';
import POSSalesReport from './POSSalesReport';
import PrintCenter from './PrintCenter';
import OperationsManual from './OperationsManual';
import CustomerGuide from '../components/CustomerGuide';
import GlobalFooter from '../components/GlobalFooter';
import FooterSettings from './FooterSettings';
import SuperAdminPortal from './SuperAdminPortal';
import DriverFeesPortal from './DriverFeesPortal';
import OrderPickupAdmin from './OrderPickupAdmin';
import OrderPickupUser from './OrderPickupUser';
import AuthCallback from './AuthCallback';
import BackupManagement from './BackupManagement';
import CustomerLocationMap from '../components/CustomerLocationMap';
import ServiceTabs from '../components/ServiceTabs';
import { categories, navItems, customerNavItems, driverNavItems } from '../data/mockData';
import { productsAPI, authAPI, brandingAPI, shoppingSessionAPI } from '../services/api';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin navigation items
const adminNavItems = [
  { id: 'pos-cashier', name: 'POS/CASHIER', icon: 'ShoppingCart', highlight: true },
  { id: 'pos-sales-report', name: 'POS Report', icon: 'Receipt', highlight: false },
  { id: 'admin-dashboard', name: 'Dashboard', icon: 'BarChart3' },
  { id: 'admin-reports', name: 'Reports', icon: 'TrendingUp' },
  { id: 'order-pickup-admin', name: 'ORDER PICKUP', icon: 'Package', highlight: 'emerald' },
  { id: 'picking-list', name: 'Picking', icon: 'ClipboardCheck' },
  { id: 'general-overseer', name: 'Overseer', icon: 'Eye' },
  { id: 'admin-products', name: 'Products', icon: 'Box' },
  { id: 'identical-products', name: 'Duplicates', icon: 'Copy' },
  { id: 'admin-orders', name: 'Orders', icon: 'ClipboardList' },
  { id: 'payment-portal', name: 'Payments', icon: 'CreditCard' },
  { id: 'stripe-settings', name: 'Stripe', icon: 'CreditCard' },
  { id: 'promo-notifications', name: 'Promotions', icon: 'Gift' },
  { id: 'branding-settings', name: 'Branding', icon: 'Palette' },
  { id: 'device-settings', name: 'Devices', icon: 'Settings' },
  { id: 'user-management', name: 'Users', icon: 'Users' },
  { id: 'role-management', name: 'Roles', icon: 'Shield' },
  { id: 'drivers-dashboard', name: 'Drivers', icon: 'Truck' },
  { id: 'system-maintenance', name: 'System', icon: 'Settings' },
  { id: 'backup-management', name: 'Backup', icon: 'Database' },
];

// Driver navigation items displayed in top bar
const driverNavItemsTop = [
  { id: 'shop', name: 'Shop', icon: 'Home' },
  { id: 'order-pickup-user', name: 'ORDER PICKUP', icon: 'Package', highlight: 'emerald' },
  { id: 'in-store-shopping', name: 'In-Store', icon: 'Store' },
  { id: 'driver-onboarding', name: 'Setup', icon: 'Truck' },
  { id: 'driver-navigation', name: 'Navigate', icon: 'Navigation' },
  { id: 'order-status', name: 'Orders', icon: 'ClipboardList' },
];

const Home = () => {
  // User state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [showSignInModal, setShowSignInModal] = useState(false);
  
  // Products state (from API)
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Branding state
  const [branding, setBranding] = useState(null);

  // Navigation state
  const [activeNav, setActiveNav] = useState('shop');
  const [trackingOrderId, setTrackingOrderId] = useState(null);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [stockFilter, setStockFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 200000]); // Default to accommodate both CAD and NGN prices
  const [showFilters, setShowFilters] = useState(false);
  const [productsPerPage, setProductsPerPage] = useState(4);
  const [cardSize, setCardSize] = useState('medium'); // small, medium, large

  // Cart & Favorites state
  const [cart, setCart] = useState([]);
  const [favorites, setFavorites] = useState([]);
  
  // User order count (from user data)
  const [userOrderCount, setUserOrderCount] = useState(0);
  
  // Barcode scanner state
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);

  // Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);

  // Picture search (PICTURES2CART) state
  const [showPictureSearch, setShowPictureSearch] = useState(false);

  // Customer Guide state
  const [showCustomerGuide, setShowCustomerGuide] = useState(false);

  // Write Review state
  const [showWriteReview, setShowWriteReview] = useState(false);

  // Shopping session tracking
  const [sessionId, setSessionId] = useState(null);

  // Navigation action state (for passing params between pages)
  const [navAction, setNavAction] = useState(null);

  // Handle navigation with optional action parameter
  const handleNavigate = (navId, action = null) => {
    setNavAction(action);
    setActiveNav(navId);
  };

  // Check for auth callback on mount - initialize from URL immediately
  // The session_id comes in the URL hash fragment: /auth/callback#session_id=xxx
  const [showAuthCallback, setShowAuthCallback] = useState(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    const urlParams = new URLSearchParams(window.location.search);
    
    // Check for session_id in hash fragment (primary) or query params (fallback)
    const hasSessionInHash = hash && hash.includes('session_id=');
    const hasSessionInQuery = urlParams.has('session_id');
    
    return path === '/auth/callback' || hasSessionInHash || hasSessionInQuery;
  });

  // Handle successful Google auth
  const handleGoogleAuthSuccess = (googleUser) => {
    setUser(googleUser);
    setShowAuthCallback(false);
    // Clean up URL
    window.history.replaceState({}, document.title, '/');
    setActiveNav('shop');
  };

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const data = await brandingAPI.get();
        setBranding(data);
      } catch (error) {
        console.error('Error fetching branding:', error);
        // Silently fail - will use defaults
      }
    };
    fetchBranding();
  }, []);

  // Track online shopping session
  useEffect(() => {
    if (user && cart.length > 0 && activeNav !== 'in-store-shopping') {
      updateOnlineSession();
    }
  }, [user, cart, activeNav]);

  // End session on unmount or when cart is cleared
  useEffect(() => {
    return () => {
      if (sessionId) {
        endOnlineSession();
      }
    };
  }, [sessionId]);

  const updateOnlineSession = async () => {
    if (!user || activeNav === 'in-store-shopping') return;
    
    const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    
    try {
      const sessionData = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        type: 'online',
        cart_items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        cart_total: total,
        status: 'active'
      };
      
      const response = await shoppingSessionAPI.upsert(sessionData, sessionId);
      if (response && response.id) {
        setSessionId(response.id);
      }
    } catch (error) {
      console.error('Error updating online session:', error);
    }
  };

  const endOnlineSession = async () => {
    if (!sessionId) return;
    try {
      await shoppingSessionAPI.end(sessionId);
      setSessionId(null);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const data = await productsAPI.getAll();
        
        // Transform API response to match frontend expectations
        const transformedProducts = data.map(p => ({
          ...p,
          inStock: p.in_stock !== false,
          available: p.available !== false,
        }));
        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products');
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Check for existing user session on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authAPI.isAuthenticated()) {
        try {
          const userData = await authAPI.getMe();
          setUser({
            id: userData.id,
            name: userData.name,
            email: userData.email,
            phone: userData.phone,
            role: userData.role,
            isAdmin: userData.is_admin,
            orderCount: userData.order_count,
          });
          setUserOrderCount(userData.order_count);
        } catch (error) {
          // Token invalid, clear it
          authAPI.logout();
        }
      }
    };
    checkAuth();
  }, []);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Filter out unavailable products (only show products with available !== false)
    result = result.filter((p) => p.available !== false);

    // Filter by search query (supports partial match with 4+ chars, barcode, or category name)
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();
      
      // Check if query matches a category name
      const matchingCategory = categories.find(c => 
        c.id !== 'all' && (
          c.id.toLowerCase().includes(query) || 
          c.name.toLowerCase().includes(query)
        )
      );
      
      if (matchingCategory && query.length >= 3) {
        // If query matches a category, filter by that category (supports multi-category)
        result = result.filter((p) => 
          p.category === matchingCategory.id || 
          (p.categories && p.categories.includes(matchingCategory.id))
        );
      } else {
        // Otherwise, search by product name, barcode, or category
        result = result.filter((p) =>
          p.name.toLowerCase().includes(query) ||
          (p.barcode && p.barcode.includes(query)) ||
          (p.category && p.category.toLowerCase().includes(query)) ||
          (p.categories && p.categories.some(c => c.toLowerCase().includes(query)))
        );
      }
    }

    // Filter by category (supports multi-category)
    if (activeCategory !== 'all') {
      result = result.filter((p) => 
        p.category === activeCategory || 
        (p.categories && p.categories.includes(activeCategory))
      );
    }

    // Filter by stock
    if (stockFilter === 'in-stock') {
      result = result.filter((p) => p.inStock);
    } else if (stockFilter === 'out-of-stock') {
      result = result.filter((p) => !p.inStock);
    }

    // Filter by price range
    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Sort products
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Keep original order for relevance
        break;
    }

    return result;
  }, [searchQuery, activeCategory, sortBy, stockFilter, priceRange, products]);

  // Auth functions
  const handleSignIn = (userData) => {
    setUser(userData);
    setToken(localStorage.getItem('auth_token'));
    setUserOrderCount(userData.orderCount || 0);
  };

  const handleSignOut = () => {
    authAPI.logout();
    setUser(null);
    setToken(null);
    setActiveNav('shop');
    toast.success('You have been signed out');
  };

  // Cart functions
  const handleAddToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    // Only show toast if not called from chat (chat has its own toast)
  };

  // Handler specifically for AI chat widget
  const handleAddToCartFromChat = (product) => {
    handleAddToCart(product);
  };

  // Favorites functions
  const handleToggleFavorite = (productId) => {
    setFavorites((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Handle viewing product details (double-click)
  const handleViewProductDetails = (product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  // Handle barcode scan result - multi-scan mode handles adding to cart
  const handleBarcodeScan = (barcode, foundProductFromScanner) => {
    // Scanner now handles multi-scan internally
    // This callback is just for logging/tracking
    console.log('Barcode scanned:', barcode, foundProductFromScanner?.name);
  };

  // Handle checkout navigation
  const handleGoToCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty!');
      return;
    }
    setActiveNav('checkout');
  };

  // Handle order completion
  const handleOrderComplete = (orderId) => {
    // Clear cart
    setCart([]);
    // Increment user order count (mock)
    setUserOrderCount(prev => prev + 1);
    // Navigate to orders
    setActiveNav('orders');
    toast.success(`Order ${orderId} placed successfully! You can track it in My Orders.`);
  };

  // Render page content based on active nav
  const renderPageContent = () => {
    switch (activeNav) {
      case 'in-store':
      case 'in-store-shopping':
        return (
          <InStoreShopping 
            user={user} 
            onNavigateToLogin={() => setShowSignInModal(true)} 
          />
        );
      case 'profile':
        return <Profile user={user} onSignOut={handleSignOut} onBack={() => setActiveNav('shop')} />;
      case 'gift-cards':
        return <GiftCards onBack={() => setActiveNav('shop')} />;
      case 'orders':
      case 'my-orders':
        return <MyOrders 
          user={user} 
          onBack={() => setActiveNav('shop')} 
          onTrackOrder={(orderId) => {
            setTrackingOrderId(orderId);
            setActiveNav('live-order-tracking');
          }}
        />;
      case 'portal':
      case 'orders-portal':
        return <OrdersPortal 
          onBack={() => setActiveNav('shop')} 
          user={user}
          onTrackOrder={(orderId) => {
            setTrackingOrderId(orderId);
            setActiveNav('live-tracking');
          }}
        />;
      case 'offers':
        return <Offers onBack={() => setActiveNav('shop')} />;
      case 'terms':
      case 'terms-conditions':
        return <TermsAndConditions onBack={() => setActiveNav('shop')} />;
      case 'help':
      case 'app-guide':
        return <AppUsersPortal onBack={() => setActiveNav('shop')} userRole={user?.role} />;
      case 'checkout':
        return (
          <Checkout 
            cart={cart}
            setCart={setCart}
            user={user}
            userOrderCount={userOrderCount}
            onBack={() => setActiveNav('shop')}
            onOrderComplete={handleOrderComplete}
            onSignIn={(mode) => {
              setActiveNav('shop');
              // Trigger sign in modal - this should be handled by parent
              if (mode === 'register') {
                toast.info('Click "Sign In" and then "Create Account" to register');
              }
            }}
          />
        );
      // Customer pages
      case 'order-tracking':
        return <OrderTracking user={user} onBack={() => setActiveNav('my-orders')} />;
      // Driver pages
      case 'driver-onboarding':
        return <DriversOnboarding onBack={() => setActiveNav('shop')} />;
      case 'driver-navigation':
        return <DriversNavigation onBack={() => setActiveNav('shop')} />;
      case 'order-status':
        return (
          <OrderStatus 
            user={user} 
            onBack={() => setActiveNav('shop')}
            onNavigateToNavigation={(order) => setActiveNav('driver-navigation')}
          />
        );
      // Admin pages
      case 'pos-cashier':
        return <POSCashier user={user} onBack={() => setActiveNav('admin-dashboard')} />;
      case 'pos-sales-report':
        return <POSSalesReport user={user} onBack={() => setActiveNav('admin-dashboard')} />;
      case 'admin-dashboard':
        return <AdminDashboard onBack={() => setActiveNav('shop')} onNavigate={handleNavigate} />;
      case 'admin-products':
        return <AdminProducts onBack={() => setActiveNav('admin-dashboard')} initialAction={navAction} onActionComplete={() => setNavAction(null)} user={user} />;
      case 'identical-products':
        return <IdenticalProductsPortal onBack={() => setActiveNav('admin-dashboard')} />;
      case 'device-settings':
        return <DeviceSettingsPortal onBack={() => setActiveNav('admin-dashboard')} />;
      case 'admin-orders':
        return <AdminOrders onBack={() => setActiveNav('admin-dashboard')} />;
      case 'payment-portal':
        return <PaymentConfirmationPortal onBack={() => setActiveNav('admin-dashboard')} />;
      case 'picking-portal':
        return <PickingPortal onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'payment-confirmation-portal':
        return <PaymentPortal onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'promo-notifications':
        return <PromotionalNotifications onBack={() => setActiveNav('admin-dashboard')} />;
      case 'branding-settings':
        return <BrandingSettings onBack={() => setActiveNav('admin-dashboard')} />;
      case 'footer-settings':
        return <FooterSettings onBack={() => setActiveNav('admin-dashboard')} />;
      case 'stripe-settings':
        return <StripeSettings onBack={() => setActiveNav('admin-dashboard')} />;
      case 'general-overseer':
        return <GeneralOverseer onBack={() => setActiveNav('admin-dashboard')} />;
      case 'drivers-dashboard':
        return (
          <DriversDashboard 
            onBack={() => setActiveNav('admin-dashboard')}
            onNavigateToOnboarding={() => setActiveNav('drivers-onboarding')}
            onNavigateToNavigation={() => setActiveNav('drivers-navigation')}
          />
        );
      case 'drivers-onboarding':
        return <DriversOnboarding onBack={() => setActiveNav('drivers-dashboard')} />;
      case 'drivers-navigation':
        return <DriversNavigation onBack={() => setActiveNav('drivers-dashboard')} />;
      case 'user-management':
        return <UserManagement onBack={() => setActiveNav('admin-dashboard')} />;
      case 'role-management':
        return <RoleManagement onBack={() => setActiveNav('admin-dashboard')} token={token} />;
      case 'order-pickup-admin':
        return <OrderPickupAdmin onBack={() => setActiveNav('admin-dashboard')} token={token} />;
      case 'order-pickup-user':
        return <OrderPickupUser onBack={() => setActiveNav('shop')} user={user} token={token} />;
      case 'system-maintenance':
        return <SystemMaintenance onBack={() => setActiveNav('admin-dashboard')} />;
      case 'backup-management':
        return <BackupManagement onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'operations-manual':
        return <OperationsManual onBack={() => setActiveNav('admin-dashboard')} />;
      case 'superadmin-portal':
        return <SuperAdminPortal onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'driver-fees':
        return <DriverFeesPortal onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'admin-reports':
        return <AdminReports onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'print-center':
        return <PrintCenter onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'picking-list':
        return <PickingList onBack={() => setActiveNav('admin-dashboard')} user={user} />;
      case 'driver-delivery':
        return <DriverDelivery onBack={() => setActiveNav('shop')} user={user} />;
      case 'live-order-tracking':
        return <LiveOrderTracking orderId={trackingOrderId} onBack={() => setActiveNav('my-orders')} user={user} />;
      case 'shop':
      default:
        return (
          <div className="relative">
            {/* Fixed Hero Banner at top */}
            <div className="fixed top-20 left-0 right-0 z-30">
              <HeroBanner branding={branding} />
            </div>
            
            {/* Spacer for fixed hero */}
            <div className="h-32 md:h-40 lg:h-44" />
            
            {/* Customer Reviews Section */}
            <ReviewsSection />
            
            {/* Customer Location Map */}
            <div className="px-4 md:px-8 lg:px-16 py-4">
              <CustomerLocationMap storeAddress="MERIVALE ROAD, Ottawa, ON" />
            </div>
            
            {/* Scrollable Content */}
            <div className="relative z-20 bg-white">
              <SearchBar 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery}
                onScanBarcode={() => setShowBarcodeScanner(true)}
                onPictureSearch={() => setShowPictureSearch(true)}
              />
              <FilterBar
                sortBy={sortBy}
                setSortBy={setSortBy}
                stockFilter={stockFilter}
                setStockFilter={setStockFilter}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                totalResults={filteredProducts.length}
                cartCount={cartCount}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              onCartClick={handleGoToCheckout}
              cardSize={cardSize}
              setCardSize={setCardSize}
            />
            <CategoryPills
              categories={categories}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
            />
            {/* Price Notice */}
            <div className="mx-4 md:mx-8 lg:mx-16 mt-4 mb-2">
              <p className="text-xs text-gray-600 font-medium bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                📌 NOTE: PRICES ARE PER ITEM UNLESS OTHERWISE STATED & PRICES ARE SUBJECT TO CHANGE WITHOUT NOTICE
              </p>
            </div>
            <ProductGrid
              products={filteredProducts}
              onAddToCart={handleAddToCart}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              onViewDetails={handleViewProductDetails}
              productsPerPage={productsPerPage}
              onProductsPerPageChange={setProductsPerPage}
              cardSize={cardSize}
            />
            </div>
          </div>
        );
    }
  };

  // Determine which nav items to show based on user role
  const getNavItemsForUser = () => {
    if (!user) return navItems;
    if (user.role === 'driver') return driverNavItemsTop;
    if (user.isAdmin || user.role === 'admin') return navItems;
    return customerNavItems;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar 
        navItems={getNavItemsForUser()}
        adminNavItems={adminNavItems}
        activeNav={activeNav} 
        setActiveNav={setActiveNav}
        user={user}
        onSignInClick={() => setShowSignInModal(true)}
        onSignOut={handleSignOut}
        branding={branding}
        onCustomerGuideClick={() => setShowCustomerGuide(true)}
        onWriteReviewClick={() => setShowWriteReview(true)}
      />
      
      {renderPageContent()}

      <SignInModal
        isOpen={showSignInModal}
        onClose={() => setShowSignInModal(false)}
        onSignIn={handleSignIn}
      />

      {/* Customer Guide Modal */}
      <CustomerGuide 
        isOpen={showCustomerGuide} 
        onClose={() => setShowCustomerGuide(false)} 
      />

      {/* Write Review Modal (Store Review) */}
      {user && token && (
        <WriteReviewModal
          isOpen={showWriteReview}
          onClose={() => setShowWriteReview(false)}
          product={null}
          token={token}
          onSuccess={() => {
            setShowWriteReview(false);
            // Could trigger a refresh of reviews section here
          }}
        />
      )}

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={showProductDetail}
        onClose={() => {
          setShowProductDetail(false);
          setSelectedProduct(null);
        }}
        onAddToCart={handleAddToCart}
        isFavorite={selectedProduct ? favorites.includes(selectedProduct.id) : false}
        onToggleFavorite={handleToggleFavorite}
        user={user}
        token={token}
      />

      {/* Floating Checkout Button - Dead Center */}
      {activeNav === 'shop' && cart.length > 0 && (
        <FloatingCheckoutButton 
          cart={cart} 
          onClick={handleGoToCheckout}
          branding={branding}
        />
      )}

      {/* Camera Barcode Scanner - Multi-scan mode */}
      <CameraBarcodeScanner
        isOpen={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={handleBarcodeScan}
        products={products}
        onAddToCart={handleAddToCart}
      />

      {/* PICTURES2CART - AI Image Search */}
      <PictureSearch
        isOpen={showPictureSearch}
        onClose={() => setShowPictureSearch(false)}
        mode="cart"
        onAddToCart={handleAddToCart}
        products={products}
      />

      {/* Google Auth Callback Handler */}
      {showAuthCallback && (
        <AuthCallback
          onAuthSuccess={handleGoogleAuthSuccess}
          onClose={() => {
            setShowAuthCallback(false);
            window.history.replaceState({}, document.title, '/');
          }}
        />
      )}

      {/* AI Shopping Assistant Widget */}
      <AIChatWidget onAddToCart={handleAddToCartFromChat} />

      {/* Global Footer */}
      <GlobalFooter />

      <Toaster position="bottom-right" />
    </div>
  );
};

export default Home;
